import React from "react";
import PluginRegistry from "../../pluginsInfrastructure/PluginRegistry";
import {
  ComponentToBeRegistered,
  Placeholder,
  PlaceholderPlugin,
  PluginEnableContext,
  SupportedPluginContentfulDataType,
} from "../../pluginsInfrastructure/PluginRegistryTypes";
import loadable from "@loadable/component";
import { productOptions } from "../pluginOwners";
import { ProductConfiguratorProps } from "./ProductConfiguratorContainer";
import { ButtonsAndPricingExperimentContext } from "./experimentContext";
import PageConfig from "../../utils/PageConfig";
import { getQueryParametersFromUrl } from "../../utils/urlHelper";

const allowListedMpvIds = new Set([
  "flyersandLeaflets",
  "foldedLeaflets",
  "postcards",
  "retractableBanners",
  "flags",
  "customStickers",
]);

export const isForcedTest = (): boolean => {
  if (PageConfig.isSSR) return false;

  const queryParams = getQueryParametersFromUrl();
  const paramEntry = Object.entries(queryParams).find(
    entry => entry[0].toLowerCase() === "unwindingconfigurator"
  );
  const entryValue = paramEntry?.[1] ?? "false";
  return entryValue.toLowerCase() === "true" || entryValue === "1";
};

export const ProductConfiguratorUnwindingComponent: ComponentToBeRegistered<ProductConfiguratorProps> = {
  placeholder: Placeholder.ProductConfigurator,
  Component: loadable<ProductConfiguratorProps>(() => import("./ProductConfiguratorContainer")),
  name: "ProductOptionsProductUnwindingConfigurator",
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    return isForcedTest();
  },
  dataNeeds: {
    contentful: {
      bulkOrderQuantity: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Bulk Order > Quantity",
      },
      quantity: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Quantity",
      },
      recommended: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "callout.Recommended",
      },
      outOfStockButtonAttribute: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page v2 > Out Of Stock",
      },
      newCalloutText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "callout.New",
      },
      dropdownIncompatibleMessage: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Dropdown Incompatible Option Message",
      },
      selectorIncompatibleText: {
        contentType: SupportedPluginContentfulDataType.DictionaryEntry,
        contentfulName: "Product Page > Selector Incompatible Text",
      },
    },
  },
};

export const ProductConfiguratorTestComponent = {
  ...ProductConfiguratorUnwindingComponent,
  name: "ProductOptionsProductTestConfigurator",
  experimentContext: ButtonsAndPricingExperimentContext,
  enable: (context: PluginEnableContext, parentComponents: string[]) => {
    const { pageProps } = context;
    const { mpv, country } = pageProps;

    // We want to exclude flags in FR
    const isFranceFlags = mpv.mpvId === "flags" && country.toLowerCase() === "fr";

    return !isFranceFlags && allowListedMpvIds.has(mpv.mpvId);
  },
};

export const productConfiguratorTest: PlaceholderPlugin = {
  init(pluginRegistry: PluginRegistry) {
    pluginRegistry.addComponent(this, ProductConfiguratorTestComponent);
    pluginRegistry.addComponent(this, ProductConfiguratorUnwindingComponent);
  },
  pluginOwner: productOptions,
};
